import { ReactNode } from 'react';
import { StatData } from '../contracts/data.contract';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

interface StatCardProps {
  data: StatData;
  icon?: ReactNode;
  className?: string;
}

export function StatCard({ data, icon, className }: StatCardProps) {
  const { value, label, trend, trendLabel, status } = data;

  const isPositive = trend && trend > 0;
  const isNegative = trend && trend < 0;

  const trendColor = isPositive ? 'text-success' : isNegative ? 'text-danger' : 'text-on-surface-variant';
  const TrendIcon = isPositive ? ArrowUpRight : isNegative ? ArrowDownRight : Minus;

  const statusBg = {
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    danger: 'bg-danger/10 text-danger',
    info: 'bg-info/10 text-info',
    default: 'bg-surface-variant text-on-surface-variant',
  }[status || 'default'];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-label-caps uppercase tracking-wider text-on-surface-variant">{label}</span>
        {icon && (
          <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', statusBg)}>
            {icon}
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-headline-lg font-semibold tabular-nums text-on-surface">{value}</span>

        {trend !== undefined && (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-1.5 py-0.5 text-body-sm font-medium',
              isPositive ? 'bg-success/10' : isNegative ? 'bg-danger/10' : 'bg-surface-variant',
              trendColor,
            )}
          >
            <TrendIcon className="mr-0.5 h-3 w-3" />
            {Math.abs(trend)}%
          </span>
        )}
      </div>

      {trendLabel && <span className="text-body-sm text-on-surface-variant">{trendLabel}</span>}
    </div>
  );
}
